'use client';

import { useEffect, useState } from 'react';
import { RotateCw, Sparkles } from 'lucide-react';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { getAppUpdateCopy } from '@/i18n/app-update-copy';
import { autoReloadHiddenTabOnce } from '@/lib/deploy-skew-reload';
import { cn } from '@/lib/utils';

/**
 * Shown instead of the error page when the open tab is out of date with a new
 * deploy. A reload fixes it; a tab in the background reloads itself once.
 */
export function AppUpdateScreen({ compact = false }: { compact?: boolean }) {
  const copy = getAppUpdateCopy(useLocale());
  const [reloading, setReloading] = useState(false);
  // Inside a component boundary the page already has its own top heading.
  const Heading = compact ? 'h2' : 'h1';

  useEffect(() => {
    if (autoReloadHiddenTabOnce()) return;
    const onVisibilityChange = () => {
      autoReloadHiddenTabOnce();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const reload = () => {
    setReloading(true);
    window.location.reload();
  };

  return (
    <div
      data-app-update-screen="true"
      className={cn(
        'bg-background flex items-center justify-center px-6',
        compact ? 'p-4' : 'min-h-dvh'
      )}
    >
      <div className="w-full max-w-md text-center">
        <div className="bg-muted/40 text-muted-foreground mx-auto mb-5 flex size-12 items-center justify-center rounded-full border">
          <Sparkles className="size-5" aria-hidden="true" />
        </div>
        <Heading
          className={cn('mb-2 font-semibold', compact ? 'text-lg' : 'text-2xl')}
        >
          {copy.title}
        </Heading>
        <p className="text-muted-foreground mb-6">{copy.description}</p>
        <Button type="button" onClick={reload} disabled={reloading}>
          {reloading ? (
            <Loader className="size-4" />
          ) : (
            <RotateCw className="size-4" aria-hidden="true" />
          )}
          {copy.reload}
        </Button>
      </div>
    </div>
  );
}
